import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Mail, Layout, Menu, X, ChevronRight, PanelLeft, Shield, LogOut, TestTube, RefreshCw, Power } from 'lucide-react';
import { UserButton, useClerk } from '@clerk/clerk-react';
import ThemeToggle from './ThemeToggle';
import { oauthApi, OAuthTokens, UserInfo } from '../services/oauthApi';
import { AdBanner } from './ads';
import toast from 'react-hot-toast';

const DashboardLayout: React.FC = () => {

    const clerk = useClerk();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Connection State
    const [oauthTokens, setOauthTokens] = useState<OAuthTokens | null>(null);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [isOAuthLoading, setIsOAuthLoading] = useState(false);
    const [isTestingConnection, setIsTestingConnection] = useState(false);

    const isTemplatesPage = location.pathname.includes('/templates');

    // Load OAuth details from localStorage
    useEffect(() => {
        const loadOAuthData = () => {
            try {
                const tokensStr = localStorage.getItem('oauth_tokens');
                const userInfoStr = localStorage.getItem('oauth_user_info');
                if (tokensStr && userInfoStr) {
                    setOauthTokens(JSON.parse(tokensStr));
                    setUserInfo(JSON.parse(userInfoStr));
                } else {
                    setOauthTokens(null);
                    setUserInfo(null);
                }
            } catch (error) {
                console.error('Error loading OAuth data:', error);
            }
        };

        loadOAuthData();

        // Silent check on mount to refresh if needed
        const silentCheck = async () => {
            const tokensStr = localStorage.getItem('oauth_tokens');
            const userInfoStr = localStorage.getItem('oauth_user_info');
            if (tokensStr && userInfoStr) {
                const tokens: OAuthTokens = JSON.parse(tokensStr);
                const info: UserInfo = JSON.parse(userInfoStr);
                try {
                    const result = await oauthApi.testConnection(tokens.access_token, info.email);
                    if (result.success && result.accessToken && result.accessToken !== tokens.access_token) {
                        console.log('Syncing refreshed token from mount check...');
                        const updatedTokens = { ...tokens, access_token: result.accessToken };
                        localStorage.setItem('oauth_tokens', JSON.stringify(updatedTokens));
                        setOauthTokens(updatedTokens);
                    }
                } catch (e) {
                    console.error('Silent connection check failed:', e);
                }
            }
        };
        silentCheck();

        // Periodically sync and listen for storage events
        const interval = setInterval(loadOAuthData, 2000); // Sync often for UI responsiveness
        window.addEventListener('storage', loadOAuthData);
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', loadOAuthData);
        };
    }, []);



    const handleOAuthLogin = async () => {
        console.log('[DashboardLayout] handleOAuthLogin called');
        setIsOAuthLoading(true);
        try {
            console.log('[DashboardLayout] Calling oauthApi.getAuthUrl()');
            const response = await oauthApi.getAuthUrl();
            console.log('[DashboardLayout] oauthApi.getAuthUrl() response:', response);
            if (response.success && response.authUrl) {
                console.log('[DashboardLayout] Auth URL received, storing return URL and redirecting');
                sessionStorage.setItem('oauth_return_url', window.location.href);
                window.location.href = response.authUrl;
            } else {
                console.error('[DashboardLayout] Failed to get OAuth URL:', response);
                toast.error('Failed to get OAuth URL');
            }
        } catch (error) {
            console.error('[DashboardLayout] OAuth login failed with error:', error);
            toast.error('OAuth login failed');
        } finally {
            console.log('[DashboardLayout] handleOAuthLogin finished');
            setIsOAuthLoading(false);
        }
    };

    const handleOAuthLogout = () => {
        localStorage.removeItem('oauth_tokens');
        localStorage.removeItem('oauth_user_info');
        setOauthTokens(null);
        setUserInfo(null);
        toast.success('Disconnected successfully');
    };

    const testConnection = async (isSilent = false) => {
        if (!oauthTokens || !userInfo) {
            if (!isSilent) toast.error('Not connected');
            return;
        }
        if (!isSilent) setIsTestingConnection(true);
        try {
            const result = await oauthApi.testConnection(oauthTokens.access_token, userInfo.email);
            if (result.success) {
                if (!isSilent) toast.success('Connection active!');

                // If backend returned a different token, it was refreshed. Sync it.
                if (result.accessToken && result.accessToken !== oauthTokens.access_token) {
                    const updatedTokens = { ...oauthTokens, access_token: result.accessToken };
                    localStorage.setItem('oauth_tokens', JSON.stringify(updatedTokens));
                    setOauthTokens(updatedTokens);
                    if (!isSilent) console.log('Access token updated after refresh');
                }
            } else {
                if (!isSilent) toast.error(result.message || 'Connection failed');
            }
        } catch (error: any) {
            if (!isSilent) toast.error(error.response?.data?.message || 'Connection check failed');
        } finally {
            if (!isSilent) setIsTestingConnection(false);
        }
    };

    const navItems = [
        {
            name: 'Compose',
            path: '/app',
            icon: Mail,
            active: !isTemplatesPage
        },
        {
            name: 'Templates',
            path: '/app/templates',
            icon: Layout,
            active: isTemplatesPage
        }
    ];

    return (
        <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
            {/* Sidebar Desktop */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 transform ${isSidebarOpen ? 'w-64' : 'w-20'
                    } hidden md:flex flex-col overflow-hidden`}
            >
                {/* Sidebar Header / Brand */}
                <div className={`h-16 flex items-center border-b border-gray-100 dark:border-gray-800 shrink-0 transition-all duration-300 ${isSidebarOpen ? 'px-6 justify-between' : 'px-0 justify-center'}`}>
                    <Link to="/" className={`flex items-center group overflow-hidden ${isSidebarOpen ? '' : 'hidden'}`}>
                        <img src="./logo.png" className='w-20 h-20' alt="Lucky Send" />
                    </Link>

                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className={`p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${!isSidebarOpen ? 'w-12 h-12 flex items-center justify-center' : ''}`}
                    >
                        <PanelLeft className={`w-5 h-5 transition-transform duration-300 ${isSidebarOpen ? '' : 'rotate-180'}`} />
                    </button>
                </div>

                {/* Sidebar Nav */}
                <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto custom-scrollbar">
                    {navItems.map((item) => (
                        <Link
                            key={item.name}
                            to={item.path}
                            className={`flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 group relative ${item.active
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm ring-1 ring-blue-200/50 dark:ring-blue-800/50'
                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <item.icon className={`w-5 h-5 shrink-0 ${item.active ? 'text-blue-600 dark:text-blue-400' : 'group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} />
                            {isSidebarOpen && (
                                <span className="ml-3 font-medium whitespace-nowrap opacity-100 transition-opacity duration-300">
                                    {item.name}
                                </span>
                            )}
                            {!isSidebarOpen && (
                                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                                    {item.name}
                                </div>
                            )}
                        </Link>
                    ))}

                    {/* Connection Status Widget in Sidebar */}
                    <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                        {isSidebarOpen ? (
                            <div className="px-2">
                                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4 px-2">
                                    Mail Server
                                </h4>
                                 {!userInfo ? (
                                     <button
                                         onClick={handleOAuthLogin}
                                         disabled={isOAuthLoading}
                                         className="w-full flex items-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg group"
                                     >
                                         <Shield className="w-4 h-4 mr-3" />
                                         {isOAuthLoading ? (
                                             <RefreshCw className="w-4 h-4 mr-3 animate-spin" />
                                         ) : (
                                             <span className="text-sm font-medium">Connect Gmail</span>
                                         )}
                                     </button>
                                 ) : (
                                    <div className="space-y-2">
                                        <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border border-green-100 dark:border-green-900/50 rounded-xl relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 w-16 h-16 bg-green-500/5 blur-2xl rounded-full -mr-8 -mt-8" />
                                            <div className="flex items-center relative z-10">
                                                <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] animate-pulse mr-3 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-[11px] font-bold text-green-800 dark:text-green-300 truncate">
                                                        {userInfo.email}
                                                    </p>
                                                    <p className="text-[9px] text-green-600 dark:text-green-400 font-medium uppercase tracking-tight">
                                                        Authenticated
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <button
                                                onClick={() => testConnection()}
                                                disabled={isTestingConnection}
                                                className="flex items-center justify-center w-full py-2.5 px-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-sm font-semibold shadow-sm active:scale-95"
                                            >
                                                {isTestingConnection ? <RefreshCw className="w-3 h-3 animate-spin" /> : <TestTube className="w-4 h-4 mr-2.5 text-blue-500" />}
                                                Test Connectivity
                                            </button>
                                            <button
                                                onClick={handleOAuthLogout}
                                                className="flex items-center justify-center w-full py-2.5 px-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-200 dark:hover:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all text-sm font-semibold shadow-sm active:scale-95"
                                            >
                                                <LogOut className="w-4 h-4 mr-2.5" />
                                                Log Out Account
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center space-y-4">
                                {!userInfo ? (
                                    <button
                                        onClick={handleOAuthLogin}
                                        className="p-3 bg-blue-600 text-white rounded-xl shadow-md hover:scale-110 transition-transform"
                                        title="Connect Gmail"
                                    >
                                        <Shield className="w-5 h-5" />
                                    </button>
                                ) : (
                                    <div className="flex flex-col items-center space-y-3">
                                        <div className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.6)]" title="Connected" />
                                        <button
                                            onClick={() => testConnection()}
                                            className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:scale-110 active:scale-95 transition-all shadow-sm"
                                            title="Test Connection"
                                        >
                                            <TestTube className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={handleOAuthLogout}
                                            className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:scale-110 active:scale-95 transition-all shadow-sm"
                                            title="Disconnect Account"
                                        >
                                            <LogOut className="w-5 h-5" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </nav>

                {/* Sidebar Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
                    {/* Sidebar Ad Unit */}
                    {isSidebarOpen && (
                        <div className="px-3 pb-4">
                            <AdBanner
                                slot={process.env.REACT_APP_ADSENSE_SIDEBAR_SLOT || "sidebar_vertical_001"}
                                format="vertical"
                                minHeight="200px"
                                className="rounded-xl overflow-hidden shadow-inner bg-gray-50/50 dark:bg-gray-800/20"
                            />
                        </div>
                    )}

                    <button
                        onClick={() => clerk.signOut(() => { window.location.href = '/'; })}
                        className={`w-full flex items-center px-3 py-2.5 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group relative ${!isSidebarOpen ? 'justify-center' : ''}`}
                    >
                        <Power className={`w-5 h-5 shrink-0 transition-colors ${!isSidebarOpen ? '' : 'mr-3'}`} />
                        {isSidebarOpen && <span className="text-sm font-medium">Sign Out</span>}
                        {!isSidebarOpen && (
                            <div className="absolute left-full ml-2 px-2 py-1 bg-red-600 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap border-none">
                                Sign Out
                            </div>
                        )}
                    </button>

                </div>
            </aside>

            {/* Main Content Area */}
            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
                {/* Top Header */}
                <header className="h-16 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40 transition-colors duration-300 px-4 sm:px-6 lg:px-8">
                    <div className="h-full flex justify-between items-center">
                        <div className="flex items-center">
                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="md:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>

                            {/* Breadcrumb or Page Title (optional) */}
                            <div className="hidden md:flex items-center text-sm text-gray-500 dark:text-gray-400 ml-4 md:ml-0">
                                <span>Dashboard</span>
                                <ChevronRight className="w-4 h-4 mx-2" />
                                <span className="text-gray-900 dark:text-white font-medium">
                                    {isTemplatesPage ? 'Templates' : 'Compose'}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <ThemeToggle />
                            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-2"></div>
                            <UserButton
                                afterSignOutUrl="/"
                                appearance={{
                                    elements: {
                                        avatarBox: "w-9 h-9 border-2 border-white dark:border-gray-800 shadow-sm"
                                    }
                                }}
                            />
                        </div>
                    </div>
                </header>

                {/* Mobile Navigation Menu */}
                {isMobileMenuOpen && (
                    <div className="fixed inset-0 z-50 md:hidden overflow-hidden">
                        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
                        <nav className="absolute top-0 left-0 bottom-0 w-64 bg-white dark:bg-gray-900 shadow-2xl flex flex-col animate-in slide-in-from-left duration-300">
                            <div className="h-16 flex items-center px-6 border-b border-gray-100 dark:border-gray-800 shrink-0">
                                <Link to="/" className="flex items-center group">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
                                        <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <span className="ml-3 font-bold text-xl bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
                                        Lucky Send
                                    </span>
                                </Link>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="ml-auto p-2 rounded-lg text-gray-500"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="flex-1 px-4 py-6 space-y-2">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.path}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${item.active
                                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm ring-1 ring-blue-200/50 dark:ring-blue-800/50'
                                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200'
                                            }`}
                                    >
                                        <item.icon className={`w-5 h-5 mr-3 ${item.active ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                                        <span className="font-medium">{item.name}</span>
                                    </Link>
                                ))}

                                {/* Mobile Connection Section */}
                                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 px-2 text-center">
                                  {!userInfo ? (
                                          <button onClick={handleOAuthLogin} className="btn-primary w-full">
                                              {isOAuthLoading ? (
                                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                              ) : (
                                                  <span>Connect Gmail</span>
                                              )}
                                          </button>
                                      ) : (
                                        <div className="space-y-4">
                                            <p className="text-xs text-gray-500">{userInfo.email}</p>
                                            <div className="flex gap-2">
                                                <button onClick={() => testConnection()} className="btn-secondary flex-1 text-xs">Test</button>
                                                <button onClick={handleOAuthLogout} className="btn-secondary flex-1 text-xs text-red-600">Off</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-auto p-4 border-t border-gray-100 dark:border-gray-800">
                                    <button
                                        onClick={() => clerk.signOut(() => { window.location.href = '/'; })}
                                        className="w-full flex items-center px-4 py-3 rounded-xl text-gray-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Power className="w-5 h-5 mr-3" />
                                        <span className="font-medium">Logout from Lucky Send</span>
                                    </button>
                                </div>
                            </div>
                        </nav>
                    </div>
                )}

                <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto flex flex-col">
                    <div className="max-w-7xl mx-auto space-y-8 flex-1 w-full">


                        <Outlet />
                    </div>

                </main>

                {/* Fixed Google Ad Banner - Pinned to bottom of screen */}
                <div className="z-30 bg-white/90 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200 dark:border-gray-800 p-2 shadow-[0_-8px_30px_rgb(0,0,0,0.08)]">
                    <div className="max-w-4xl mx-auto px-4">
                        <AdBanner
                            slot={process.env.REACT_APP_ADSENSE_FOOTER_SLOT || "dashboard_footer_001"}
                            format="horizontal"
                            minHeight="90px"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardLayout;
